<template>
  <div>
    <div class="flex flex-wrap -mx-2">

      <div class="w-full md:w-1/2 mb-3 px-2">
        <wz-select
          label="Empresa"
          required
          :items="companies"
          :value="form.companyId"
          :disabled="lockCompany"
          defaultOption="Elegir empresa"
          :error="v.companyId.$error && !v.companyId.required ? 'Empresa es requerida' : ''"
          @change="onCompanySelected"
        />
      </div>

      <div class="w-full md:w-1/2 mb-3 px-2">
        <wz-select
          label="Sede"
          v-model="form.workSiteId"
          :items="workSites"
          defaultOption="Elegir sede"
        />
      </div>

      <div class="w-full md:w-1/2 mb-3 px-2">
        <wz-select
          label="Área"
          required
          v-model="form.areaId"
          :items="areas"
          defaultOption="Elegir área"
          :error="v.areaId.$error && !v.areaId.required ? 'Área es requerida' : ''"
        />
      </div>

      <div class="w-full md:w-1/2 mb-3 px-2">
        <wz-select
          label="Cargo"
          required
          v-model="form.positionId"
          :items="positions"
          defaultOption="Elegir cargo"
          :error="v.positionId.$error && !v.positionId.required ? 'Cargo es requerido' : ''"
        />
      </div>

      <div class="w-full md:w-1/2 mb-3 px-2">
        <wz-select
          label="Canal"
          required
          v-model="form.channelId"
          :items="channels"
          defaultOption="Elegir canal"
          :error="v.channelId.$error && !v.channelId.required ? 'Canal es requerido' : ''"
        />
      </div>

      <div class="w-full md:w-1/2 mb-3 px-2">
        <wz-select
          label="Línea de gasto"
          required
          v-model="form.expenseLineId"
          :items="expenseLines"
          defaultOption="Elegir línea de gasto"
          :error="v.expenseLineId.$error && !v.expenseLineId.required ? 'Línea de gasto es requerida' : ''"
        />
      </div>

      <div class="w-full md:w-1/2 mb-3 px-2">
        <wz-select-search
          label="Jefe directo"
          v-model="form.directManagerId"
          :items="employees"
          defaultOption="Elegir jefe directo"
          search-placeholder="Buscar por nombre o documento..."
        />
      </div>

      <div class="w-full md:w-1/2 mb-3 px-2">
        <wz-input
          label="Responsable de RRHH"
          type="email"
          v-model="form.hrResponsibleEmail"
          placeholder="Ingresar correo del responsable de RRHH"
        />
      </div>

      <div class="w-full md:w-1/2 mb-3 px-2">
        <wz-select
          label="Horario de trabajo"
          v-model="form.scheduleId"
          :items="schedules"
          defaultOption="Elegir horario"
        />
        <p v-if="selectedSchedule" class="text-muted text-sm mt-1 mb-0">
          {{ selectedScheduleSummary }}
        </p>
      </div>

    </div>
  </div>
</template>

<script>
const DAY_SHORT = { 1:'Lun', 2:'Mar', 3:'Mié', 4:'Jue', 5:'Vie', 6:'Sáb', 7:'Dom' }
const DAY_FIELDS = [
  { key: 1, name: 'monday' },
  { key: 2, name: 'tuesday' },
  { key: 3, name: 'wednesday' },
  { key: 4, name: 'thursday' },
  { key: 5, name: 'friday' },
  { key: 6, name: 'saturday' },
  { key: 7, name: 'sunday' },
]

export default {
  props: {
    form:      { type: Object, required: true },
    v:         { type: Object, required: true },
    companies: { type: Array,  default: () => [] },
    workSites: { type: Array,  default: () => [] },
    areas:     { type: Array,  default: () => [] },
    positions: { type: Array,  default: () => [] },
    employees:    { type: Array,  default: () => [] },
    schedules:    { type: Array,  default: () => [] },
    channels:     { type: Array,  default: () => [] },
    expenseLines: { type: Array,  default: () => [] },
    lockCompany:  { type: Boolean, default: false },
  },
  emits: ['company-changed'],
  computed: {
    selectedSchedule() {
      return this.schedules.find(s => s.key === this.form.scheduleId) || null
    },
    selectedScheduleSummary() {
      const s = this.selectedSchedule
      if (!s) return ''
      const groups = this.buildDayGroups(s)
      if (groups.length) {
        return groups.map(g => {
          const label = g.from === g.to ? DAY_SHORT[g.from] : `${DAY_SHORT[g.from]}-${DAY_SHORT[g.to]}`
          const hours = g.eff ? ` (${g.eff}h)` : ''
          return `${label} ${g.start}-${g.end}${hours}`
        }).join(' · ')
      }
      // Fallback: horarios antiguos sin datos por día → resumen global
      const days = (s.workDaysList || []).map(d => DAY_SHORT[d] || d).join('-')
      if (!days) return ''
      const eff = s.effectiveHours ? `, ${s.effectiveHours}h efectivas` : ''
      return `${days} ${s.entryTime || ''}-${s.exitTime || ''}${eff}`
    },
  },
  methods: {
    // Agrupa días consecutivos con horario idéntico usando los campos por día.
    buildDayGroups(s) {
      const groups = []
      let cur = null
      for (const d of DAY_FIELDS) {
        const start = s[`${d.name}Start`]
        const end   = s[`${d.name}End`]
        if (!start || !end) { cur = null; continue } // día no laborable: corta la racha
        const breakStart = s[`${d.name}BreakStart`] || null
        const breakEnd   = s[`${d.name}BreakEnd`] || null
        if (cur && cur.start === start && cur.end === end
            && cur.breakStart === breakStart && cur.breakEnd === breakEnd) {
          cur.to = d.key
        } else {
          cur = { from: d.key, to: d.key, start, end, breakStart, breakEnd, eff: this.dayEffectiveHours(start, end, breakStart, breakEnd) }
          groups.push(cur)
        }
      }
      return groups
    },
    dayEffectiveHours(start, end, breakStart, breakEnd) {
      if (!start || !end) return ''
      const toMin = t => { const [h, m] = t.split(':').map(Number); return h * 60 + m }
      let mins = toMin(end) - toMin(start)
      if (mins < 0) mins += 24 * 60
      if (breakStart && breakEnd) {
        let b = toMin(breakEnd) - toMin(breakStart)
        if (b < 0) b += 24 * 60
        mins -= b
      }
      if (mins <= 0) return ''
      return parseFloat((mins / 60).toFixed(2))
    },
    onCompanySelected(companyId) {
      this.form.companyId  = companyId
      this.form.workSiteId = null
      this.$emit('company-changed', companyId)
    },
  },
}
</script>

<style lang="stylus" scoped></style>
